import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { ReviewBoardService } from '@services/review-board.service';
import { ErrorDialogService } from '@services/error-dialog.service';
import { ReviewBoardActions } from '@store/actions/review-board/review-board.actions';
import { parseProblemDetailsError } from '../../../utils/api-error.util';

@Injectable()
export class ReviewBoardEffects {
  loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewBoardActions.loadList),
      switchMap(() =>
        this.reviewBoardService.getList().pipe(
          map((items) => ReviewBoardActions.loadListSuccess({ items })),
          catchError((error: HttpErrorResponse) =>
            of(ReviewBoardActions.loadListFailure({
              error: parseProblemDetailsError(error, 'Failed to load MIPS review list'),
            }))
          )
        )
      )
    )
  );

  loadStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewBoardActions.loadStatus),
      switchMap(({ submissionId }) =>
        this.reviewBoardService.getStatus(submissionId).pipe(
          map((status) => ReviewBoardActions.loadStatusSuccess({ status })),
          catchError((error: HttpErrorResponse) =>
            of(ReviewBoardActions.loadStatusFailure({
              error: parseProblemDetailsError(error, 'Failed to load review status'),
            }))
          )
        )
      )
    )
  );

  requestReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewBoardActions.requestReview),
      switchMap(({ submissionId }) =>
        this.reviewBoardService.requestReview(submissionId).pipe(
          map((status) => ReviewBoardActions.requestReviewSuccess({ status })),
          catchError((error: HttpErrorResponse) =>
            of(ReviewBoardActions.requestReviewFailure({
              error: parseProblemDetailsError(error, 'Failed to request review'),
              submissionId,
              isConflict: error.status === 409,
            }))
          )
        )
      )
    )
  );

  cancelReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewBoardActions.cancelReview),
      switchMap(({ submissionId }) =>
        this.reviewBoardService.cancelReview(submissionId).pipe(
          map((status) => ReviewBoardActions.cancelReviewSuccess({ status })),
          catchError((error: HttpErrorResponse) =>
            of(ReviewBoardActions.cancelReviewFailure({
              error: parseProblemDetailsError(error, 'Failed to cancel review'),
              submissionId,
              isConflict: error.status === 409,
            }))
          )
        )
      )
    )
  );

  recoverFromReviewConflict$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewBoardActions.requestReviewFailure, ReviewBoardActions.cancelReviewFailure),
      filter(({ isConflict }) => isConflict),
      map(({ submissionId }) => ReviewBoardActions.loadStatus({ submissionId }))
    )
  );

  showReviewActionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ReviewBoardActions.requestReviewFailure,
          ReviewBoardActions.cancelReviewFailure,
        ),
        filter(({ isConflict }) => !isConflict),
        tap(({ error }) => {
          this.errorDialogService.showError(error, 'Review Action Error');
        })
      ),
    { dispatch: false }
  );

  showLoadFailureError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ReviewBoardActions.loadListFailure,
          ReviewBoardActions.loadStatusFailure,
        ),
        tap(({ error }) => {
          this.errorDialogService.showError(error, 'MIPS Review Error');
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private reviewBoardService: ReviewBoardService,
    private errorDialogService: ErrorDialogService,
  ) {}
}
